/**
 * Вкладка «Кому что нужно» раздела «Экология» панели администратора (ecology-catalog, макет
 * 16.09.2026): матрица «документ × категория НВОС I–IV и „без категории“» с галочками обязательности,
 * справочное «Условие» и «Когда обновлять» (виды изменений и документы, после которых позиция требует
 * актуализации, — из справочника, только чтение). Правки копятся
 * локально и сохраняются одной кнопкой «Сохранить» — матрица уходит на сервер целиком и сохраняется
 * одной транзакцией. Строки — позиции, которые ведутся в пульте площадки: у остальных (абонентское
 * обслуживание, лицензия) обязательность по категориям ни на что не влияет.
 */
import { useEffect, useMemo, useState } from 'react'
import { adminApi, type EcoCatalogItem, type EcoCategory } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  ECO_CATEGORIES,
  ECO_CATEGORY_HINTS,
  ECO_CATEGORY_LABELS,
  ECO_CONDITION_PRESETS,
  ECO_UPDATE_TRIGGER_SHORT_LABELS,
  toggleCanonical,
} from '@/lib/ecology'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Checkbox } from '@/design-system/components/primitives/checkbox'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

/** «Без условия»: Radix Select не допускает пустое значение пункта. */
const NO_CONDITION = 'none'

interface MatrixRow {
  categories: EcoCategory[]
  conditionNote: string
}

function sameRow(a: MatrixRow, b: MatrixRow): boolean {
  return a.conditionNote === b.conditionNote && a.categories.join() === b.categories.join()
}

/**
 * «Когда обновлять»: виды изменений и «после: <документ>» (ПЭК, НДВ, НМУ — после инвентаризации);
 * у отчётности и документа без правил — прочерк.
 */
function updateLabel(item: EcoCatalogItem, names: Map<string, string>): string {
  if (item.kind !== 'document') return '—'
  const parts: string[] = []
  if (item.updateTriggers.length > 0) {
    parts.push(item.updateTriggers.map((t) => ECO_UPDATE_TRIGGER_SHORT_LABELS[t]).join(', '))
  }
  const after = item.dependsOn.map((id) => names.get(id)).filter((n): n is string => Boolean(n))
  if (after.length > 0) parts.push(`после: ${after.join(', ')}`)
  return parts.length > 0 ? parts.join('; ') : '—'
}

export function MatrixTab({
  items,
  onItemsChange,
}: {
  items: EcoCatalogItem[]
  onItemsChange: (items: EcoCatalogItem[]) => void
}) {
  const rows = useMemo(() => items.filter((i) => i.tracked), [items])
  const names = useMemo(() => new Map(items.map((i) => [i.id, i.name])), [items])
  // Несохранённые правки по id позиции; строки без правок берутся из справочника.
  const [edits, setEdits] = useState<Record<string, MatrixRow>>({})
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Справочник поменялся (правка в модалке, сохранение) — правки, совпавшие с ним или
  // относящиеся к удалённым позициям, больше не «несохранённые».
  useEffect(() => {
    setEdits((prev) => {
      const next: Record<string, MatrixRow> = {}
      for (const item of items) {
        const edit = prev[item.id]
        if (edit && !sameRow(edit, item)) next[item.id] = edit
      }
      return next
    })
  }, [items])

  useEffect(() => {
    if (!saved) return
    const timer = window.setTimeout(() => setSaved(false), 2500)
    return () => window.clearTimeout(timer)
  }, [saved])

  const dirty = Object.keys(edits).length > 0

  function rowOf(item: EcoCatalogItem): MatrixRow {
    return edits[item.id] ?? { categories: item.categories, conditionNote: item.conditionNote }
  }

  function change(item: EcoCatalogItem, next: MatrixRow) {
    setSaved(false)
    setEdits((prev) => {
      const copy = { ...prev }
      if (sameRow(next, item)) delete copy[item.id]
      else copy[item.id] = next
      return copy
    })
  }

  async function save() {
    setError(null)
    setSaving(true)
    try {
      const updated = await adminApi.ecology.saveMatrix(
        rows.map((item) => ({ id: item.id, ...rowOf(item) })),
      )
      setEdits({})
      onItemsChange(updated)
      setSaved(true)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить матрицу'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <Card
        className="mb-4"
        header={
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>Кому какие документы нужны</span>
            <div className="flex items-center gap-3">
              {saved ? (
                <span role="status" className="text-13 font-medium text-green">
                  Сохранено
                </span>
              ) : null}
              <Button size="sm" onClick={() => void save()} disabled={!dirty || saving}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        }
      >
        <p className="mb-3 text-12 text-txt-tertiary">
          Галочка — документ обязателен для площадки этой категории. «Условие» уточняет, когда
          документ нужен независимо от категории. Стартовый список — из справочника услуг
          19.08.2026, корректируется экологом.
        </p>
        {error ? (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        ) : null}
        <div className="overflow-x-auto">
          <DataTable>
            <DataTableHead>
              <tr>
                <DataTableHeaderCell>Документ</DataTableHeaderCell>
                {ECO_CATEGORIES.map((c) => (
                  <DataTableHeaderCell key={c} className="text-center">
                    {ECO_CATEGORY_LABELS[c]}
                  </DataTableHeaderCell>
                ))}
                <DataTableHeaderCell>Условие</DataTableHeaderCell>
                <DataTableHeaderCell>Когда обновлять</DataTableHeaderCell>
              </tr>
            </DataTableHead>
            <DataTableBody>
              {rows.map((item) => {
                const row = rowOf(item)
                // Значение не из готового списка (введено раньше) остаётся доступным к выбору.
                const conditions =
                  row.conditionNote && !ECO_CONDITION_PRESETS.includes(row.conditionNote)
                    ? [...ECO_CONDITION_PRESETS, row.conditionNote]
                    : ECO_CONDITION_PRESETS
                return (
                  <DataTableRow key={item.id}>
                    <DataTableCell className="font-medium text-txt">{item.name}</DataTableCell>
                    {ECO_CATEGORIES.map((c) => (
                      <DataTableCell key={c} className="text-center">
                        <Checkbox
                          checked={row.categories.includes(c)}
                          aria-label={`${item.name} — ${c === 'none' ? 'без категории' : `категория ${c}`}`}
                          onCheckedChange={(v) =>
                            change(item, {
                              ...row,
                              categories: toggleCanonical(
                                ECO_CATEGORIES,
                                row.categories,
                                c,
                                v === true,
                              ),
                            })
                          }
                        />
                      </DataTableCell>
                    ))}
                    <DataTableCell>
                      <Select
                        value={row.conditionNote || NO_CONDITION}
                        onValueChange={(v) =>
                          change(item, { ...row, conditionNote: v === NO_CONDITION ? '' : v })
                        }
                      >
                        <SelectTrigger
                          className="min-w-64 text-left"
                          aria-label={`${item.name} — условие`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_CONDITION}>без условия</SelectItem>
                          {conditions.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </DataTableCell>
                    <DataTableCell className="text-13 text-txt-secondary">
                      {updateLabel(item, names)}
                    </DataTableCell>
                  </DataTableRow>
                )
              })}
            </DataTableBody>
          </DataTable>
        </div>
      </Card>

      <Card header="Категории объектов НВОС — подсказка">
        <ul>
          {ECO_CATEGORIES.map((c) => (
            <li
              key={c}
              className="flex items-center gap-3 border-b border-line py-2.5 last:border-b-0"
            >
              <Badge variant="gray" className="min-w-10 justify-center">
                {ECO_CATEGORY_LABELS[c]}
              </Badge>
              <span className="text-13 text-txt-secondary">{ECO_CATEGORY_HINTS[c]}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  )
}
